import { useState } from 'react';

import {
  BREAKPOINTS,
  useMediaQuery,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkOverlay, { OVERLAY_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-overlay';
import BpkSkeleton, {
  IMAGE_SKELETON_STYLE,
  SKELETON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-skeleton';
import BpkSwitch, { SWITCH_VARIANTS } from '@skyscanner-internal/backpack-web/bpk-component-switch';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import BpkVisuallyHidden from '@skyscanner-internal/backpack-web/bpk-component-visually-hidden';
import { translate } from '@web-platform/shared-universal-translations';
import { useImagePreloader } from '@web-platform/shared-universal-utils-frontend/src/helpers/hooks/useImagePreloader';

import { SponsoredInfoButton } from '../../SponsoredInfoComponents/SponsoredInfoButton/SponsoredInfoButton';
import { getSponsoredCallToActionData } from '../../SponsoredInfoComponents/getSponsoredCallToActionData';
import { handleToggleEnterKey } from '../handleToggleEnterKey';

import STYLES from './CollapsedHeader.module.scss';

type CollapsedHeaderProps = {
  heroImageUrl: string;
  partnerLogo: string;
  mobilePartnerLogo: string;
  partnerName: string;
  titleText: string;
  isToggleOn: boolean;
  onToggleChange: () => void;
  pioPartnerName: string;
  pioAgencyName?: string;
};

const CollapsedHeader = ({
  heroImageUrl,
  isToggleOn,
  mobilePartnerLogo,
  onToggleChange,
  partnerLogo,
  partnerName,
  pioAgencyName,
  pioPartnerName,
  titleText,
}: CollapsedHeaderProps) => {
  const isTabletOrSmaller = !!useMediaQuery(BREAKPOINTS.TABLET);
  const isNarrowScreen = !!useMediaQuery(BREAKPOINTS.MOBILE);
  const [logoError, setLogoError] = useState(false);
  const imageLoaded = useImagePreloader(heroImageUrl);

  const toggleLabel = translate('ktxt_ad_show_partner_flights_only', { partner: partnerName });
  const onToggleKeyDown = handleToggleEnterKey(onToggleChange);

  const callToActionData = getSponsoredCallToActionData({ pioPartnerName, pioAgencyName });

  const sponsoredInfoIcon = (
    <SponsoredInfoButton
      infoIconCustomColor
      callToAction={callToActionData}
      ariaLabel={`${translate('ktxt_partnership_with', {
        partner_name: partnerName,
      })}. ${translate('ktxt_a11y_Ad_info_button')}`}
    >
      <BpkText tagName="span" className={STYLES.partnerText} aria-hidden="true">
        {translate('homepageHero_in_partnership_with', { partner: partnerName })}
      </BpkText>
    </SponsoredInfoButton>
  );

  return (
    <div className={STYLES.container} data-testid="sponsored-header-collapsed">
      {!imageLoaded && (
        <div className={STYLES.skeletonContainer}>
          <BpkSkeleton
            type={SKELETON_TYPES.image}
            size={{ width: '100%', height: '100%' }}
            style={IMAGE_SKELETON_STYLE.rounded}
          />
        </div>
      )}
      {imageLoaded && (
        <>
          <img src={heroImageUrl} alt="" className={STYLES.heroImage} />
          <BpkOverlay
            overlayType={OVERLAY_TYPES.rightHigh}
            className={STYLES.overlay}
            foregroundContent={null}
          >
            {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
            <></>
          </BpkOverlay>
          {isNarrowScreen && (
            <BpkOverlay
              overlayType={OVERLAY_TYPES.rightHigh}
              className={STYLES.overlay}
              foregroundContent={null}
            >
              {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
              <></>
            </BpkOverlay>
          )}
          <div className={STYLES.content}>
            <BpkVisuallyHidden as="h2">
              {translate('ktxt_a11y_sponsored_partner_message', {
                partner: partnerName,
                message: titleText,
              })}
            </BpkVisuallyHidden>
            {/* Desktop layout: wordmark + partnership badge stacked left, headline centered, toggle pill right */}
            {!isNarrowScreen && !isTabletOrSmaller && (
              <>
                <div className={STYLES.leftSection}>
                  {logoError ? (
                    <BpkText
                      textStyle={TEXT_STYLES.label1}
                      className={STYLES.logoFallback}
                      aria-hidden="true"
                    >
                      {partnerName}
                    </BpkText>
                  ) : (
                    <img
                      src={partnerLogo}
                      alt=""
                      aria-hidden
                      className={STYLES.logo}
                      onError={() => setLogoError(true)}
                    />
                  )}
                  <div className={STYLES.sponsoredButton}>{sponsoredInfoIcon}</div>
                </div>
                <BpkText
                  textStyle={TEXT_STYLES.heading2}
                  className={STYLES.headline}
                  aria-hidden="true"
                >
                  {titleText}
                </BpkText>
                <label className={STYLES.togglePill}>
                  <BpkSwitch
                    ariaLabel={toggleLabel}
                    checked={isToggleOn}
                    onChange={onToggleChange}
                    onKeyDown={onToggleKeyDown}
                    variant={SWITCH_VARIANTS.onContrast}
                    small
                  />
                  <BpkText textStyle={TEXT_STYLES.label1} className={STYLES.toggleLabel}>
                    {toggleLabel}
                  </BpkText>
                </label>
              </>
            )}

            {/* Tablet layout: square logo + copy (title + badge), toggle pill right */}
            {isTabletOrSmaller && !isNarrowScreen && (
              <>
                <div className={STYLES.leftSection}>
                  {logoError ? (
                    <BpkText
                      textStyle={TEXT_STYLES.label1}
                      className={STYLES.logoFallback}
                      aria-hidden="true"
                    >
                      {partnerName}
                    </BpkText>
                  ) : (
                    <img
                      src={mobilePartnerLogo}
                      alt=""
                      aria-hidden
                      className={STYLES.logo}
                      onError={() => setLogoError(true)}
                    />
                  )}
                  <div className={STYLES.tabletCopy}>
                    <BpkText
                      textStyle={TEXT_STYLES.label1}
                      className={STYLES.tabletTitle}
                      aria-hidden="true"
                    >
                      {titleText}
                    </BpkText>
                    <div className={STYLES.sponsoredButton}>{sponsoredInfoIcon}</div>
                  </div>
                </div>
                <label className={STYLES.togglePill}>
                  <BpkSwitch
                    ariaLabel={toggleLabel}
                    checked={isToggleOn}
                    onChange={onToggleChange}
                    onKeyDown={onToggleKeyDown}
                    variant={SWITCH_VARIANTS.onContrast}
                    small
                  />
                  <BpkText textStyle={TEXT_STYLES.label2} className={STYLES.toggleLabel}>
                    {toggleLabel}
                  </BpkText>
                </label>
              </>
            )}

            {/* Mobile layout: square logo + copy (toggle text + badge), switch on right (no pill) */}
            {isNarrowScreen && (
              <>
                <div className={STYLES.leftSection}>
                  {logoError ? (
                    <BpkText
                      textStyle={TEXT_STYLES.label1}
                      className={STYLES.logoFallback}
                      aria-hidden="true"
                    >
                      {partnerName}
                    </BpkText>
                  ) : (
                    <img
                      src={mobilePartnerLogo}
                      alt=""
                      aria-hidden
                      className={STYLES.logo}
                      onError={() => setLogoError(true)}
                    />
                  )}
                  <div className={STYLES.mobileCopy}>
                    <BpkText textStyle={TEXT_STYLES.label2} className={STYLES.mobileToggleText}>
                      {toggleLabel}
                    </BpkText>
                    <div className={STYLES.sponsoredButton}>{sponsoredInfoIcon}</div>
                  </div>
                </div>
                <BpkSwitch
                  ariaLabel={toggleLabel}
                  checked={isToggleOn}
                  onChange={onToggleChange}
                  onKeyDown={onToggleKeyDown}
                  variant={SWITCH_VARIANTS.onContrast}
                  small
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export { CollapsedHeader };
export type { CollapsedHeaderProps };
